import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Phone, CheckCircle, RefreshCw } from 'lucide-react';

// Google Icon Component
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AuthPage = () => {
  const { t, language } = useLanguage();
  const { user, login, register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ target: 50 });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    isOwner: false,
  });

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Check for error in URL (from failed Google auth)
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'google_auth_failed') {
      toast.error(language === 'fr' ? 'Échec de la connexion Google. Veuillez réessayer.' : 'Google sign-in failed. Please try again.');
    }
  }, [searchParams, language]);

  useEffect(() => {
    generateCaptcha();
  }, [mode]);

  const generateCaptcha = () => {
    // Generate random target between 20 and 80
    const target = Math.floor(Math.random() * 60) + 20;
    setCaptchaQuestion({ target });
    setCaptchaAnswer('');
    setCaptchaVerified(false);
  };

  const verifyCaptcha = () => {
    // Slider captcha - already verified when sliding
    if (captchaVerified) {
      return true;
    }
    toast.error(language === 'fr' ? 'Veuillez compléter la vérification' : 'Please complete the verification');
    return false;
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!verifyCaptcha()) return;

    // Strict email format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(language === 'fr' ? 'Format d\'email invalide' : 'Invalid email format');
      return;
    }
    
    setLoading(true);
    
    try {
      if (mode === 'register') {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }
        
        await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone || null,
          role: formData.isOwner ? 'owner' : 'user',
        });
        toast.success('Compte créé avec succès !');
      } else {
        await login(formData.email, formData.password);
        toast.success('Connexion réussie !');
      }
      navigate('/dashboard');
    } catch (error) {
      // Robust error handling — never crash to white screen
      let message = t('error');
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        message = detail[0].msg;
      } else if (error?.message) {
        message = error.message;
      }
      toast.error(message);
      generateCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/30">
      <Card className="w-full max-w-md" data-testid="auth-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-2xl">R</span>
          </div>
          <CardTitle className="font-heading text-2xl">
            {mode === 'login' ? t('loginTitle') : t('registerTitle')}
          </CardTitle>
          <CardDescription>
            {mode === 'login' 
              ? 'Accédez à votre espace personnel' 
              : 'Créez votre compte Roomia.net'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
              {mode === 'login' && (
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                    {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
                  </Link>
                </div>
              )}
            </div>

            {mode === 'register' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')} (optionnel)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+237..."
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isOwner"
                    checked={formData.isOwner}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isOwner: checked }))}
                    data-testid="owner-checkbox"
                  />
                  <Label htmlFor="isOwner" className="text-sm">{t('ownerAccount')}</Label>
                </div>
              </>
            )}

            {/* Captcha Slider */}
            <div className="space-y-3 p-4 bg-secondary/50 rounded-lg border">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  {language === 'fr' ? 'Vérification de sécurité' : 'Security verification'}
                </Label>
                {captchaVerified && (
                  <div className="flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    {language === 'fr' ? 'Vérifié' : 'Verified'}
                  </div>
                )}
              </div>
              
              {!captchaVerified ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? `Faites glisser le curseur jusqu'à ${captchaQuestion.target}`
                      : `Slide to ${captchaQuestion.target}`}
                  </p>
                  <div className="relative">
                    <div className="h-12 bg-background rounded-lg border-2 border-dashed border-muted-foreground/30 relative overflow-hidden">
                      <div 
                        className="absolute left-0 top-0 h-full bg-primary/20 transition-all duration-100"
                        style={{ width: `${(parseInt(captchaAnswer) / 100) * 100}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                        {captchaAnswer || '0'} / 100
                      </div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={captchaAnswer || 0}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setCaptchaAnswer(value.toString());
                        if (Math.abs(value - captchaQuestion.target) <= 3) {
                          setCaptchaVerified(true);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      data-testid="captcha-slider"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0</span>
                    <span className="text-primary font-medium">
                      {language === 'fr' ? 'Cible:' : 'Target:'} {captchaQuestion.target}
                    </span>
                    <span>100</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 py-3 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">
                    {language === 'fr' ? 'Vérification réussie!' : 'Verification successful!'}
                  </span>
                </div>
              )}
              
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={generateCaptcha}
                className="w-full text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                {language === 'fr' ? 'Nouveau défi' : 'New challenge'}
              </Button>
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading || !captchaVerified} data-testid="submit-btn">
              {loading && <Loader2 className="h-5 w-5 mr-2 animate-spin" />}
              {mode === 'login' ? t('login') : t('register')}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {language === 'fr' ? 'Ou continuer avec' : 'Or continue with'}
              </span>
            </div>
          </div>

          {/* Google OAuth Button */}
          <Button 
            type="button"
            variant="outline" 
            className="w-full h-12 gap-3"
            onClick={() => {
              setGoogleLoading(true);
              googleLogin();
            }}
            disabled={googleLoading}
            data-testid="google-login-btn"
          >
            {googleLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            {language === 'fr' ? 'Continuer avec Google' : 'Continue with Google'}
          </Button>

          {/* Toggle Mode */}
          <p className="text-center text-sm text-muted-foreground">
            {mode === 'login' ? t('noAccount') : t('alreadyAccount')}{' '}
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-primary hover:underline font-medium"
              data-testid="toggle-mode-btn"
            >
              {mode === 'login' ? t('register') : t('login')}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
